import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { format } from 'date-fns';
import find from 'lodash/find';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import {
  CareCategories,
  ClientNames,
  Constants,
} from '../../../../../../common/Constants';
import { ConstantsLagoon } from '../../../../../../common/ConstantsLagoon';
import { useHarvardPilgrimEligibility } from '../../../../../../common/GuestHeader/utils';
import { PortalIdentifier, PortalName } from '../../../../../../common/Portal';
import { PSXIcon } from '../../../../../../common/PSXIcon';
import { isMnrMember } from '../../../../../../common/Utils/memberUtils/memberUtils';
import { resolvePremiumCareDisclaimer } from '../../../../../../common/Utils/premiumCareDisclaimer';
import { PortalContext } from '../../../../../../context/PortalContext';
import { useCustomDisclaimerMessage } from '../../../../../../hooks/useCustomDisclaimerMessage';
import { useFeatureFlag } from '../../../../../../hooks/useFeatureFlag/useFeatureFlag';
import { useLagoon } from '../../../../../../hooks/useLagoon';
import { useSmartChoice } from '../../../../../../hooks/useSmartChoice';
import { useChipStore } from '../../../../../../store/useChipStore';
import { ChipState } from '../../../../../../store/useChipStore/chipStore';
import { useGuestStore } from '../../../../../../store/useGuestStore';
import { GuestState } from '../../../../../../store/useGuestStore/guestStore';
import { DateTime } from '../../../../../../utils/DateTime';
import { shouldUseGehaV2Styling } from '../../../../../../utils/gehaThemeUtils';
import {
  getCurrentMember,
  isCNSMember,
} from '../../../../../../utils/user.utils';
import { getLanguage } from '../../../../context/Internationalization/helpers';
import {
  BENEFIT_ATTRIBUTES,
  CONVENIENCE_ATTRIBUTES,
  PERSONAL_FIT_ATTRIBUTES,
  QUALITY_ATTRIBUTES,
} from '../../CompareDrawer/utility/compareDrawerConstants';
import { PrintSection, PrintValue } from '../../Print/PrintView.style';
import { PrintExpandedBenefitsCard } from './PrintExpandedBenefitsCard';
import {
  BoxContainer,
  DisclaimerContainer,
  PrintViewDivider,
  PrintViewProviderBoldText,
  PrintViewProviderMediumText,
  PrintViewProviderNormalText,
  StyledMarkdown,
} from './PrintExpandedCardStyle.styled';
import { PrintExpandedConvenienceCard } from './PrintExpandedConvenienceCard';
import { PrintExpandedGeneralCard } from './PrintExpandedGeneralCard';
import { PrintExpandedPersonalFitCard } from './PrintExpandedPersonalFitCard';
import { PrintExpandedQualityCard } from './PrintExpandedQualityCard';

export const PrintCompareShareDrawer = ({
  selectedProviders,
  selectedAttributes,
  preferencesData,
}) => {
  const { chipValue } = useChipStore(
    useShallow((state: ChipState) => ({
      chipValue: state.chipValue,
    }))
  );
  const { lineOfBusiness, reciprocityId: currentMemberReciprocityId } =
    getCurrentMember();
  const { portalData } = useContext(PortalContext);
  const isMNR = isMnrMember(lineOfBusiness);
  const isCNS = isCNSMember(lineOfBusiness);
  const isLaww =
    portalData?.portalName === PortalName.LAWW ||
    portalData?.portalName === PortalName.LAWW_GUEST;
  const smartChoiceEligibility = useSmartChoice();
  const isSmartChoice = smartChoiceEligibility.isEnabled;
  const isMNROrCNSMember = isMNR || isCNS;
  const { t } = useTranslation();

  const lagoonData = useLagoon('ui-messaging')();
  const printViewContent = find(lagoonData, {
    key: isLaww
      ? ConstantsLagoon.HEALTHGRADES.PRINT_VIEW_CONTENT
      : ConstantsLagoon.HEALTHGRADES.DISCLAIMER_IN_PAGE,
  });
  const customDisclaimerInPageMessage = useCustomDisclaimerMessage(
    Constants.CUSTOM_DISCALIMER_BANNER_PAGE_NAME.HEALTH_GRADES_IN_PAGE
  );
  const printViewContentMessage =
    customDisclaimerInPageMessage ?? printViewContent?.message ?? '';

  const printViewContentMessageWithLineBreaks = isLaww
    ? printViewContentMessage
    : printViewContentMessage.replace(/\n/g, '<br/>');

  const smartChoiceDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_DISCLAIMER,
  });

  const visionDirectoryDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.VISION_DICTIONARY.DISCLAIMER_CONTENT,
  });

  const visionDirectoryDisclaimerAddlContent = find(lagoonData, {
    key: ConstantsLagoon.VISION_DICTIONARY.DISCLAIMER_ADDL_CONTENT,
  });

  const dentalMnrDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.MNR_DISCLAIMER.DENTAL_MNR_DISCLAIMER,
  });

  const medicalFacilityMnrDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.MNR_DISCLAIMER.MEDICAL_FACILITY_MNR_DISCLAIMER,
  });

  const visionMnrDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.MNR_DISCLAIMER.VISION_MNR_DISCLAIMER,
  });

  const directoryMnrDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.MNR_DISCLAIMER.DIRECTORY_MNR_DISCLAIMER,
  });
  const noComparison = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_NO_COMPARISON,
  })?.message;

  const locale = getLanguage().code;
  const { content: premiumDisclaimerContent } = resolvePremiumCareDisclaimer({
    locale,
    lagoonData,
  });

  const shouldDisplayTitle = (
    selectedAttributes: any,
    attributesToCheck: string[]
  ) =>
    selectedAttributes?.some(
      (item: any) => item?.isChecked && attributesToCheck.includes(item?.name)
    );

  const renderVisionDisclaimer = () => {
    if (chipValue === Constants.CHIPS_CATEGORIES.VISION) {
      return `${visionDirectoryDisclaimer?.message ?? ''}
              <br><br/>
              ${visionDirectoryDisclaimerAddlContent?.message ?? ''}`;
    }

    if (isLaww) return '';

    return premiumDisclaimerContent;
  };

  const renderMnrDisclaimer = () => {
    switch (chipValue) {
      case CareCategories.DENTAL:
        return dentalMnrDisclaimer?.message.replace(/\n/g, '<br/>');
      case CareCategories.MEDICAL_SPECIALISTS:
      case CareCategories.FACILITIES:
        return medicalFacilityMnrDisclaimer?.message.replace(/\n/g, '<br/>');
      case CareCategories.ALL:
        return directoryMnrDisclaimer?.message.replace(/\n/g, '<br/>');
      case CareCategories.VISION:
        return visionMnrDisclaimer?.message.replace(/\n/g, '<br/>');
      default:
        return '';
    }
  };

  const isGeha = portalData?.portalIdentifier === PortalIdentifier.GEHA;
  const isGEHAV2 = shouldUseGehaV2Styling();
  const isGehaTheme = isGEHAV2 || isGeha;

  const clientName = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.CLIENT_NAME
  );
  const normalizedClientName = (clientName || portalData?.clientName || '')
    .trim()
    .toLowerCase();
  const hasEmpireHeaderLogo =
    typeof document !== 'undefined' &&
    Boolean(
      document.querySelector('[data-testid="guest-empire-logo-abyss-icon"]')
    );
  const isEmpireGuest =
    normalizedClientName.includes(ClientNames.EMPIRE) || hasEmpireHeaderLogo;

  const { guestPlanSelectorData } = useGuestStore(
    useShallow((state: GuestState) => ({
      guestPlanSelectorData: state.guestPlanSelectorData,
    }))
  );
  const portalReciprocityId =
    guestPlanSelectorData?.delsysCode || currentMemberReciprocityId;

  const [isHarvardPilgrimLogoEnabled] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_HARVARD_PILGRIM_LOGO,
  ]);
  const isEligibleForHarvardPilgrim =
    useHarvardPilgrimEligibility(portalReciprocityId);
  const isHarvardPilgrim =
    isHarvardPilgrimLogoEnabled && isEligibleForHarvardPilgrim;

  let brandConfig;

  if (isLaww) {
    brandConfig = {
      affiliate: 'optum',
      name: 'optum',
      color: 'orange',
      icon: 'optum_logo',
    };
  } else {
    brandConfig = {
      affiliate: 'uhc',
      name: 'uhc',
      color: '#002677',
      icon: 'uhc_logo',
    };
  }

  const renderSmartChoiceDisclaimer = () => (
    <>
      <br />
      <StyledMarkdown>{smartChoiceDisclaimer?.message ?? ''}</StyledMarkdown>
    </>
  );

  return (
    <PrintSection>
      <Flex alignContent="flex-start" direction="column">
        <Flex justify="space-between">
          {isHarvardPilgrim ? (
            <PSXIcon icon="hphc_logo" />
          ) : isGehaTheme ? (
            <PSXIcon
              css={{ height: '52px' }}
              icon={isGehaTheme ? 'geha_brandingV2' : 'geha_branding'}
              size={200}
            />
          ) : isEmpireGuest ? (
            <PSXIcon icon="empire_logo" />
          ) : (
            <PSXIcon color={brandConfig?.color} icon={brandConfig?.icon} />
          )}
        </Flex>
        <PrintViewDivider orientation="horizontal" />
        <Flex justify="space-between">
          <PrintViewProviderMediumText color="black">
            {`${chipValue} Providers`}
          </PrintViewProviderMediumText>
          <Flex>
            <PrintViewProviderBoldText color="black">
              {t('COMPARE.Date')}:&nbsp;
            </PrintViewProviderBoldText>
            <PrintViewProviderNormalText color="black">
              {format(DateTime.getCurrentDateTime(), 'MMMM dd, yyyy')}
            </PrintViewProviderNormalText>
          </Flex>
        </Flex>
        <PrintViewDivider orientation="horizontal" />
        {selectedProviders?.length ? (
          <>
            <PrintExpandedGeneralCard
              selectedAttributes={selectedAttributes}
              selectedProviders={selectedProviders}
            />
            {isSmartChoice &&
              shouldDisplayTitle(selectedAttributes, QUALITY_ATTRIBUTES) && (
                <Layout.Stack grow>
                  <BoxContainer>{t('COMPARE.Quality')}</BoxContainer>
                </Layout.Stack>
              )}
            <PrintExpandedQualityCard
              isSmartChoice={isSmartChoice}
              selectedAttributes={selectedAttributes}
              selectedProviders={selectedProviders}
            />
            {isSmartChoice &&
              shouldDisplayTitle(selectedAttributes, BENEFIT_ATTRIBUTES) && (
                <Layout.Stack grow>
                  <BoxContainer>{t('COMPARE.Benefits')}</BoxContainer>
                </Layout.Stack>
              )}
            <PrintExpandedBenefitsCard
              isSmartChoice={isSmartChoice}
              selectedAttributes={selectedAttributes}
              selectedProviders={selectedProviders}
            />
            {isSmartChoice &&
              shouldDisplayTitle(
                selectedAttributes,
                CONVENIENCE_ATTRIBUTES
              ) && (
                <Layout.Stack grow>
                  <BoxContainer>{t('COMPARE.Convenience')}</BoxContainer>
                </Layout.Stack>
              )}
            <PrintExpandedConvenienceCard
              preferencesData={preferencesData}
              selectedAttributes={selectedAttributes}
              selectedProviders={selectedProviders}
            />
            {isSmartChoice &&
              shouldDisplayTitle(
                selectedAttributes,
                PERSONAL_FIT_ATTRIBUTES
              ) && (
                <Layout.Stack grow>
                  <BoxContainer>{t('COMPARE.Personal_Fit')}</BoxContainer>
                </Layout.Stack>
              )}

            <PrintExpandedPersonalFitCard
              isSmartChoice={isSmartChoice}
              preferencesData={preferencesData}
              selectedAttributes={selectedAttributes}
              selectedProviders={selectedProviders}
            />
          </>
        ) : (
          <PrintValue> {noComparison}</PrintValue>
        )}
      </Flex>
      <Flex alignContent="center" css={{ display: 'flex' }}>
        <DisclaimerContainer alignContent="flex-start" direction="column">
          {/* Lagoon data disclaimer */}
          <div
            dangerouslySetInnerHTML={{
              __html: isMNR
                ? renderMnrDisclaimer()
                : printViewContentMessageWithLineBreaks,
            }}
          />
          {isSmartChoice && renderSmartChoiceDisclaimer()}
          <div
            dangerouslySetInnerHTML={{
              __html: isMNROrCNSMember ? '' : renderVisionDisclaimer(),
            }}
            style={{ marginTop: '10px' }}
          />
        </DisclaimerContainer>
      </Flex>
    </PrintSection>
  );
};
